import { API_BASE_URL } from '~/env';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | null,
    message: string,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
}

/**
 * Thin fetch wrapper. Sessions ride on an HttpOnly cookie
 * (`seven_session`) that the server sets on /auth/login + /auth/verify-email
 * and clears on /auth/logout, so there's no bearer token for JS to touch;
 * `credentials: 'include'` tells the browser to send the cookie
 * cross-origin, and the API's CORS config whitelists this origin for
 * credentialed reads. Nest's `{ message: { code, message } }` error shape
 * is unwrapped so callers see a single ApiError regardless of the envelope.
 */
export async function api<T = unknown>(
  path: string,
  opts: ApiOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    accept: 'application/json',
  };
  if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
  }

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });

  const text = await res.text();
  const parsed: unknown = text ? safeJson(text) : null;

  if (!res.ok) {
    const { code, message } = extractError(parsed, res.statusText);
    throw new ApiError(res.status, code, message, parsed);
  }

  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractError(
  body: unknown,
  fallback: string,
): { code: string | null; message: string } {
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    const nested = b.message;
    if (nested && typeof nested === 'object') {
      const n = nested as Record<string, unknown>;
      return {
        code: typeof n.code === 'string' ? n.code : null,
        message: typeof n.message === 'string' ? n.message : fallback,
      };
    }
    if (typeof b.message === 'string') {
      return { code: null, message: b.message };
    }
  }
  return { code: null, message: fallback };
}
